import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { NoAccessPhoto, Photo } from '../../constants/DocumentTypes';
import { DocumentType, useDocumentTypesQuery } from '../../queries';
import AddedFile from '../AddedFile';
import DropZone from '../DropZone';
import { PackDocument } from '../../pages/PackAdd';

interface Props {
  documents: PackDocument[];
  setDocuments: React.Dispatch<React.SetStateAction<PackDocument[]>>;
}

export default function PackDocumentationUpload({ documents, setDocuments }: Props) {
  const [documentTypes, setDocumentTypes] = useState<Partial<DocumentType>[]>([]);
  const { loading: loadingDocTypes, data } = useDocumentTypesQuery();
  const { register, setValue } = useFormContext();

  useEffect(() => {
    if (data) {
      // exclude the photo type
      setDocumentTypes(data.documentTypes.filter((dt) => dt.code !== Photo && dt.code !== NoAccessPhoto));
    }
  }, [data]);

  // having to use useEffect and reset to initialize the document types. for some obscure reason defaultValues on useform does not work
  useEffect(() => {
    if (documents && documentTypes) {
      // const defaultValues: any = {};
      documents.forEach((d, i) => {
        // defaultValues[`documents[${i}].documentTypeCode`] = d.documentTypeCode;
        setValue(`documents[${i}].documentTypeCode`, d.documentTypeCode);
      });
    }
  }, [documents, setValue, documentTypes]);

  const onFilesAdded = (selectedFiles: File[]) => {
    const newDocuments: PackDocument[] = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      // have to provide a default description otherwise React complains about controlled/uncontrolled components in console
      newDocuments.push({ file, documentTypeCode: '' });
    }
    setDocuments((prev) => [...prev, ...newDocuments]);
  };

  const onFileRemoved = (fileIndex: number) => {
    const filesCopy = Array.from(documents);
    filesCopy.splice(fileIndex, 1);
    setDocuments(filesCopy);
  };

  const onDocumentTypeChange = (
    e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
    fileIndex: number
  ) => {
    const documentTypeCode = String(e.target.value);
    setDocuments((prev) => prev.map((x, i) => (i === fileIndex ? { ...x, documentTypeCode } : x)));
  };

  // handling error manually here
  const hasError = (doc: PackDocument): boolean => {
    return doc.documentTypeCode === undefined || doc.documentTypeCode.length === 0;
  };

  return (
    <>
      <DropZone
        onFilesAdded={onFilesAdded}
        title="Upload documentation"
        accept=".pdf"
        acceptHelperText="Accepted format: PDF"
        multiple
        maxSizeMb={50}
      />

      {documents.map((document, i) => (
        <AddedFile
          key={i}
          documentType={document.documentTypeCode}
          fileName={document.file.name}
          onRemove={() => onFileRemoved(i)}
        >
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor={`documents[${i}].documentTypeCode`} error={hasError(document)}>
              Type
            </InputLabel>
            <Select
              label="Type"
              native
              inputProps={{
                name: `documents[${i}].documentTypeCode`,
                id: `documents[${i}]documentTypeCodeInput`,
              }}
              inputRef={register({ required: 'Type required' })}
              onChange={(e) => onDocumentTypeChange(e, i)}
              error={hasError(document)}
            >
              <option aria-label="None" value="" />

              {!loadingDocTypes &&
                documentTypes &&
                documentTypes.map((documentType) => (
                  <option value={documentType.code} key={documentType.code}>
                    {documentType.name}
                  </option>
                ))}
            </Select>
            <FormHelperText error={hasError(document)}>
              {hasError(document) ? 'Type is required' : undefined}
            </FormHelperText>
          </FormControl>
        </AddedFile>
      ))}
    </>
  );
}
